import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ShoppingBag, Heart, Star, Shield, Leaf, Clock, ArrowLeft, 
  ChevronRight, Sparkles, AlertCircle, Check
} from 'lucide-react';
import { 
  BASE_URL, ENDPOINTS, getImageUrl, addToCart, addToWishlist, 
  getProductReviews 
} from '../apiConfig';
import './ProductDetailsPage.css';
import OutOfStockPopup from '../components/common/OutOfStockPopup';

const isSameVariant = (v1, v2) => {
  if (!v1 || !v2) return false;
  const id1 = v1._id || v1.id;
  const id2 = v2._id || v2.id;
  if (id1 && id2) return id1 === id2;
  const n1 = v1.size || v1.volume || v1.name;
  const n2 = v2.size || v2.volume || v2.name;
  if (n1 && n2) return n1 === n2;
  return v1 === v2;
};

const ProductDetailsPage = () => {
  const { productId } = useParams();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [oosPopup, setOosPopup] = useState({ open: false, variantName: '', productName: '' });
  const [activeTab, setActiveTab] = useState('description'); // 'description' | 'reviews' | 'details'
  // Wishlist state
  const [isWishlisted, setIsWishlisted] = useState(false);

  const mainRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProductDetails();

    // Silent polling for real-time stock updates
    const pollInterval = setInterval(() => {
      fetchProductDetails(false);
    }, 5000);
    return () => clearInterval(pollInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const fetchProductDetails = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_PRODUCTS}?t=${Date.now()}`, { headers, cache: 'no-store' });
      const result = await response.json();
      const prodData = Array.isArray(result) ? result : (result?.data || []);
      const foundOriginal = prodData.find(p => (p._id || p.id) === productId);
      const found = foundOriginal ? JSON.parse(JSON.stringify(foundOriginal)) : null;
      
      if (found) {
        setProduct(prevProduct => {
          if (!prevProduct || prevProduct._id !== found._id) return found;
          
          if (showLoading) return found;

          // Silent poll: preserve locally deducted variant stock using Math.min
          const updated = { ...found };
          if (prevProduct.variants && updated.variants) {
            updated.variants = updated.variants.map(uv => {
              const pv = prevProduct.variants.find(v => isSameVariant(v, uv));
              if (pv) {
                const pStock = pv.stock !== undefined ? pv.stock : pv.quantity;
                const uStock = uv.stock !== undefined ? uv.stock : uv.quantity;
                const finalStock = Math.min(Number(pStock ?? 9999), Number(uStock ?? 9999));
                return { ...uv, stock: finalStock, quantity: finalStock };
              }
              return uv;
            });
          } else {
            const pStock = prevProduct.stock !== undefined ? prevProduct.stock : prevProduct.quantity;
            const uStock = updated.stock !== undefined ? updated.stock : updated.quantity;
            const finalStock = Math.min(Number(pStock ?? 9999), Number(uStock ?? 9999));
            updated.stock = finalStock;
            updated.quantity = finalStock;
          }
          return updated;
        });

        if (showLoading) {
          setActiveImg(found.images?.[0] || found.img);
          setSelectedVariant(found.variants?.find(v => v.isDefault) || found.variants?.[0] || null);
          
          // Fetch reviews only on initial load
          setLoadingReviews(true);
          const fetchedReviews = await getProductReviews(found._id || found.id);
          setReviews(fetchedReviews);
          setLoadingReviews(false);
        } else {
          // If silent polling, just update selectedVariant stock without changing user selection
          setSelectedVariant(prev => {
            if (!prev) return prev;
            const updatedVariant = found.variants?.find(v => isSameVariant(v, prev));
            if (!updatedVariant) return prev;
            
            const prevStock = prev.stock !== undefined ? prev.stock : prev.quantity;
            const newStock = updatedVariant.stock !== undefined ? updatedVariant.stock : updatedVariant.quantity;
            const finalStock = Math.min(Number(prevStock || 0), Number(newStock || 0));

            return { ...prev, stock: finalStock, quantity: finalStock, inStock: updatedVariant.inStock };
          });
        }

        // Check if item is wishlisted in local storage
        const wishlistItems = JSON.parse(localStorage.getItem('wishlistItems') || '[]');
        setIsWishlisted(wishlistItems.some(item => (item._id || item.id) === (found._id || found.id)));
      } else {
        if (showLoading) setProduct(null);
      }
    } catch (err) {
      console.error("Error loading product details:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Entrance animations removed as per user request

  // Thumbnail animation removed

  const handleAddToCart = async () => {
    if (!product || isAdding) return;
    const currentStockCount = (() => {
      if (selectedVariant) {
        if (selectedVariant.stock !== undefined) return Number(selectedVariant.stock);
        if (selectedVariant.quantity !== undefined) return Number(selectedVariant.quantity);
        if (selectedVariant.inStock === false || String(selectedVariant.inStock) === 'false') return 0;
      }
      if (product.stock !== undefined) return Number(product.stock);
      if (product.quantity !== undefined) return Number(product.quantity);
      return 10;
    })();

    const isOutOfStock = 
      currentStockCount === 0 || 
      (selectedVariant && (selectedVariant.inStock === false || String(selectedVariant.inStock) === 'false')) ||
      product.inStock === false || 
      product.isAvailable === false ||
      product.status === 'OutOfStock' ||
      product.stockStatus === 'OutOfStock';

    if (isOutOfStock) {
      const variantLabel = selectedVariant
        ? (selectedVariant.size || selectedVariant.volume || selectedVariant.name || '')
        : '';
      setOosPopup({ open: true, variantName: variantLabel, productName: product.name });
      return;
    }

    setIsAdding(true);
    // Snapshot the selected variant at time of click — not affected by future state changes
    const activeVar = selectedVariant 
      ? (product.variants?.find(v => isSameVariant(v, selectedVariant)) || selectedVariant)
      : (product.variants?.[0] || null);
    const variantId = activeVar ? (activeVar._id || activeVar.id) : null;
    const volumeToSend = activeVar ? (activeVar.size || activeVar.volume || activeVar.name) : (product.volume || '100ml');
    
    try {
      await addToCart(product._id || product.id, variantId, quantity, volumeToSend);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);

      // Deduct stock ONLY for the exact variant user bought
      // Use stockCount (already computed from selectedVariant or product) as reliable source
      const stockNow = (() => {
        if (activeVar) {
          if (activeVar.stock !== undefined) return Number(activeVar.stock);
          if (activeVar.quantity !== undefined) return Number(activeVar.quantity);
        }
        if (product.stock !== undefined) return Number(product.stock);
        if (product.quantity !== undefined) return Number(product.quantity);
        return stockCount; // fallback to the already-computed stockCount
      })();
      const remaining = Math.max(0, stockNow - quantity);

      if (activeVar) {
        const updatedVar = { ...activeVar, stock: remaining, quantity: remaining };
        setSelectedVariant(updatedVar);
        if (product.variants) {
          setProduct(prev => {
            if (!prev || !prev.variants) return prev;
            const newVariants = prev.variants.map(v =>
              isSameVariant(v, activeVar)
                ? { ...v, stock: remaining, quantity: remaining }
                : v
            );
            return { ...prev, variants: newVariants };
          });
        }
      } else {
        setProduct(prev => ({ ...prev, stock: remaining, quantity: remaining }));
      }
      
      // Reset quantity back to 1
      setQuantity(1);
    } catch (err) {
      console.error("Cart add failure:", err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggleWishlist = async () => {
    if (!product) return;
    const wishListItems = JSON.parse(localStorage.getItem('wishlistItems') || '[]');
    const isAlreadyWish = wishListItems.some(item => (item._id || item.id) === (product._id || product.id));

    let updatedList;
    if (isAlreadyWish) {
      updatedList = wishListItems.filter(item => (item._id || item.id) !== (product._id || product.id));
      setIsWishlisted(false);
    } else {
      updatedList = [...wishListItems, product];
      setIsWishlisted(true);
      await addToWishlist(product._id || product.id).catch(console.error);
    }
    localStorage.setItem('wishlistItems', JSON.stringify(updatedList));
    window.dispatchEvent(new Event('wishlistUpdated'));
  };

  const getRatingValue = (rating) => {
    if (!rating) return 5.0;
    if (typeof rating === 'number') return rating;
    if (rating.includes('★')) return rating.split('★').length - 1;
    return parseFloat(rating) || 5.0;
  };

  // Sync quantity cap when variant changes
  useEffect(() => {
    if (!product) return;
    const currentStock = (() => {
      if (selectedVariant) {
        if (selectedVariant.stock !== undefined) return Number(selectedVariant.stock);
        if (selectedVariant.quantity !== undefined) return Number(selectedVariant.quantity);
        if (selectedVariant.inStock === false || String(selectedVariant.inStock) === 'false') return 0;
      }
      if (product.stock !== undefined) return Number(product.stock);
      if (product.quantity !== undefined) return Number(product.quantity);
      return 10;
    })();
    
    setQuantity(prev => {
      if (currentStock > 0 && prev > currentStock) return Math.max(1, currentStock);
      return prev;
    });
  }, [selectedVariant, product]);

  if (loading) {
    return (
      <div className="pdp-loader-wrap">
        <div className="pdp-loader-spinner"></div>
        <p>RECOLLECTING ESSENCE ARCHIVES...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pdp-not-found-wrap">
        <AlertCircle size={50} color="#d4af37" />
        <h2>PRODUCT NOT FOUND</h2>
        <p>The fragrance archetype you are looking for does not exist in our archives.</p>
        <Link to="/shop" className="pdp-back-btn">
          <ArrowLeft size={16} /> <span>RETURN TO SHOP</span>
        </Link>
      </div>
    );
  }

  const displayImages = (product.images && product.images.length > 0) ? product.images : [product.img];
  const activeVariant = selectedVariant || product.variants?.[0] || {};
  const price = activeVariant.finalPrice || activeVariant.price || product.price || 0;
  const oldPrice = activeVariant.finalPrice < activeVariant.price ? activeVariant.price : null;

  // Calculate actual stock amount
  const stockCount = (() => {
    if (selectedVariant) {
      if (selectedVariant.stock !== undefined) return Number(selectedVariant.stock);
      if (selectedVariant.quantity !== undefined) return Number(selectedVariant.quantity);
      if (selectedVariant.inStock === false || String(selectedVariant.inStock) === 'false') return 0;
    }
    if (product.stock !== undefined) return Number(product.stock);
    if (product.quantity !== undefined) return Number(product.quantity);
    return 10;
  })();

  // Determine if it is out of stock
  const isOutOfStock = 
    stockCount === 0 || 
    (selectedVariant && (selectedVariant.inStock === false || String(selectedVariant.inStock) === 'false')) ||
    product.inStock === false || 
    product.isAvailable === false ||
    product.status === 'OutOfStock' ||
    product.stockStatus === 'OutOfStock';

  return (
    <div className="pdp-elite-container" ref={mainRef}>
      {/* Toast alert */}
      {showToast && (
        <div className="pdp-toast-popup">
          <div className="pdp-toast-content">
            <Check size={16} className="pdp-toast-icon" />
            <span>✓ ADDED TO CART</span>
          </div>
        </div>
      )}

      {/* Out-of-Stock Popup */}
      <OutOfStockPopup
        isOpen={oosPopup.open}
        onClose={() => setOosPopup({ open: false, variantName: '', productName: '' })}
        variantName={oosPopup.variantName}
        productName={oosPopup.productName}
        hasQuickView={false}
      />

      {/* Breadcrumbs */}
      <div className="pdp-breadcrumbs">
        <Link to="/">HOME</Link>
        <ChevronRight size={12} />
        <Link to="/shop">SHOP</Link>
        <ChevronRight size={12} />
        <span className="pdp-bread-active">{(product.name || 'PRODUCT').toUpperCase()}</span>
      </div>

      <div className="pdp-layout-grid">
        
        {/* LEFT PANEL: IMAGES */}
        <div className="pdp-visual-pane">
          <div className="pdp-visual-wrapper">
            <div className="pdp-visual-bg-glow"></div>
            
            {/* Main Interactive Zoom Box */}
            <div className="pdp-visual-main-box">
              <img 
                src={getImageUrl(activeImg)} 
                alt={product.name} 
                className="pdp-main-img" 
                ref={imgRef} 
              />
              <span className="pdp-premium-badge">PREMIUM</span>
            </div>

            {/* Thumbnail Selectors */}
            {displayImages.length > 1 && (
              <div className="pdp-thumbnail-row">
                {displayImages.map((img, idx) => (
                  <div
                    key={idx}
                    className={`pdp-thumb-card ${activeImg === img ? 'active' : ''}`}
                    onClick={() => setActiveImg(img)}
                  >
                    <img src={getImageUrl(img)} alt={`preview-${idx}`} />
                  </div>
                ))}
              </div>
            )}

            {/* Floating Luxury Stats Panel */}
            <div className="pdp-visual-stats">
              <div className="pdp-stat-item">
                <Clock size={16} className="gold" />
                <span>12H+ Long Lasting</span>
              </div>
              <div className="pdp-stat-divider"></div>
              <div className="pdp-stat-item">
                <Leaf size={16} className="gold" />
                <span>Organic Natural Base</span>
              </div>
              <div className="pdp-stat-divider"></div>
              <div className="pdp-stat-item">
                <Sparkles size={16} className="gold" />
                <span>Fast Projection</span>
              </div>
            </div>

          </div>
        </div>

        {/* RIGHT PANEL: INFO */}
        <div className="pdp-info-pane">
          <div className="pdp-info-header">
            <span className="pdp-brand-label">{product.brand || 'Escentrum Private'}</span>
            <div className="pdp-stars-row">
              <div className="pdp-stars">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    size={14} 
                    fill={i < Math.round(product.averageRating || 5) ? '#d4af37' : 'none'} 
                    color="#d4af37" 
                  />
                ))}
              </div>
              <span className="pdp-rating-num">({reviews.length} Customer Reviews)</span>
            </div>
          </div>

          <h1 className="pdp-product-name">{product.name}</h1>
          
          <div className="pdp-price-row">
            <span className="pdp-current-price">Rs. {price.toLocaleString()}</span>
            {oldPrice && <span className="pdp-old-price">Rs. {oldPrice.toLocaleString()}</span>}
          </div>

          {/* Luxury Stock Status Indicator */}
          <div className="pdp-stock-status-wrapper">
            {isOutOfStock ? (
              <span className="pdp-stock-badge out-of-stock">
                <span className="stock-dot red-pulse"></span> OUT OF STOCK
              </span>
            ) : (
              <span className="pdp-stock-badge in-stock">
                <span className="stock-dot green-pulse"></span> IN STOCK ({stockCount} items left)
              </span>
            )}
          </div>

          <div className="pdp-divider"></div>

          <p className="pdp-short-desc">
            {product.description || "A magnificent sensory voyage created by our master perfumers. Sourced from the finest local and organic elements to craft an unrivaled signature trail that projects class and luxury throughout the day."}
          </p>

          <div className="pdp-variant-selector">
            <span className="pdp-sel-label">CONCENTRATION & VOLUME:</span>
            <div className="pdp-variant-options">
              {product.variants && product.variants.length > 0 ? (
                [...product.variants].sort((a, b) => {
                  const getVol = (v) => {
                    const str = String(v.size || v.volume || v.name || "");
                    const match = str.match(/(\d+)/);
                    return match ? parseInt(match[1], 10) : 0;
                  };
                  return getVol(a) - getVol(b);
                }).map((v, idx) => {
                  const variantName = v.size || v.volume || v.name || `Variant ${idx + 1}`;
                  const isActive = selectedVariant
                    ? isSameVariant(v, selectedVariant)
                    : idx === 0;
                  const vStock = v.stock !== undefined ? Number(v.stock) : (v.quantity !== undefined ? Number(v.quantity) : null);
                  const vOos = vStock === 0 || v.inStock === false || String(v.inStock) === 'false';
                  return (
                    <button 
                      key={v._id || v.id || idx}
                      className={`pdp-vol-btn ${isActive ? 'active' : ''} ${vOos ? 'pdp-vol-oos' : ''}`}
                      onClick={() => !vOos && setSelectedVariant(v)}
                      title={vOos ? 'Out of Stock' : `${vStock ?? ''} in stock`}
                    >
                      <span className="pdp-vol-name">{variantName}</span>
                    </button>
                  );
                })
              ) : (
                <button className="pdp-vol-btn active">
                  {product.volume || '100ml'}
                </button>
              )}
            </div>
          </div>

          {/* Quantity Selector & Action Buttons */}
          <div className="pdp-purchase-bar">
            <div className="pdp-qty-selector">
              <button 
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                className="qty-btn"
                disabled={isOutOfStock || quantity <= 1}
              >
                -
              </button>
              <span className="qty-val">{isOutOfStock ? 0 : quantity}</span>
              <button 
                onClick={() => setQuantity(q => Math.min(stockCount || 1, q + 1))}
                className="qty-btn"
                disabled={isOutOfStock || quantity >= stockCount}
              >
                +
              </button>
            </div>

            <button 
              className={`pdp-add-cart-btn ${isAdding ? 'adding' : ''} ${isOutOfStock ? 'disabled-out-of-stock' : ''}`}
              onClick={handleAddToCart}
              disabled={isAdding || isOutOfStock}
            >
              <ShoppingBag size={18} />
              <span>{isAdding ? 'ADDING...' : (isOutOfStock ? 'OUT OF STOCK' : 'ADD TO CART')}</span>
            </button>

            <button 
              className={`pdp-wish-btn ${isWishlisted ? 'wishlisted' : ''}`}
              onClick={handleToggleWishlist}
            >
              <Heart size={20} fill={isWishlisted ? '#c9a227' : 'none'} color={isWishlisted ? '#c9a227' : '#0a0a0a'} />
            </button>
          </div>

          {/* Security & Authenticity guarantees */}
          <div className="pdp-guarantees">
            <div className="g-item">
              <Shield size={16} />
              <span>100% Original Scent</span>
            </div>
            <div className="g-item">
              <Leaf size={16} />
              <span>Cruelty Free & Bio</span>
            </div>
          </div>

          <div className="pdp-divider"></div>

          {/* Tabbed details */}
          <div className="pdp-tabs-container">
            <div className="pdp-tabs-nav">
              <button 
                className={`pdp-tab-trigger ${activeTab === 'description' ? 'active' : ''}`}
                onClick={() => setActiveTab('description')}
              >
                Scent Notes
              </button>
              <button 
                className={`pdp-tab-trigger ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Details & Safety
              </button>
              <button 
                className={`pdp-tab-trigger ${activeTab === 'reviews' ? 'active' : ''}`}
                onClick={() => setActiveTab('reviews')}
              >
                Reviews ({reviews.length})
              </button>
            </div>

            <div className="pdp-tab-content">
              {activeTab === 'description' && (
                <div className="pdp-notes-tab">
                  <div className="notes-block">
                    <span className="note-title">Top Notes:</span>
                    <p>Saffron, Bergamot, Pink Pepper</p>
                  </div>
                  <div className="notes-block">
                    <span className="note-title">Heart Notes:</span>
                    <p>Turkish Rose, Oud Wood, Jasmine</p>
                  </div>
                  <div className="notes-block">
                    <span className="note-title">Base Notes:</span>
                    <p>Amber, Sandalwood, Dark Musk, Vanilla</p>
                  </div>
                </div>
              )}

              {activeTab === 'details' && (
                <div className="pdp-specs-tab">
                  <div className="spec-row">
                    <span className="spec-label">Concentration</span>
                    <span className="spec-val">Eau De Parfum (EDP)</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">Scent Family</span>
                    <span className="spec-val">Oriental Woody Floral</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">Ingredients</span>
                    <span className="spec-val">Alcohol Denat., Water, Fragrance, Linalool, Limonene, Coumarin</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">Safety</span>
                    <span className="spec-val">Avoid spraying in eyes. Keep away from heat sources and open flames.</span>
                  </div>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="pdp-reviews-tab">
                  
                  {/* Reviews List */}
                  <div className="pdp-reviews-archive">
                    {loadingReviews ? (
                      <p className="no-reviews">Retrieving reviews...</p>
                    ) : reviews.length === 0 ? (
                      <p className="no-reviews">Be the first to review this magnificent creation.</p>
                    ) : (
                      reviews.map((rev, i) => (
                        <div key={rev._id || i} className="pdp-review-card">
                          <div className="pdp-rev-header">
                            <div className="pdp-rev-meta">
                              <span className="pdp-rev-author">{rev.userName || rev.user?.name || 'Anonymous'}</span>
                              <div className="pdp-rev-stars">
                                {[...Array(5)].map((_, idx) => (
                                  <Star 
                                    key={idx} 
                                    size={10} 
                                    fill={idx < getRatingValue(rev.rating) ? '#d4af37' : 'none'} 
                                    color={idx < getRatingValue(rev.rating) ? '#d4af37' : 'rgba(255, 255, 255, 0.20)'} 
                                  />
                                ))}
                              </div>
                            </div>
                            <span className="pdp-rev-date">
                              {rev.createdAt ? new Date(rev.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short', day: 'numeric'
                              }) : 'Recent'}
                            </span>
                          </div>
                          <p className="pdp-rev-text">{rev.comment || rev.reviewText || 'An outstanding perfume signature.'}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default ProductDetailsPage;
